import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { App, AppState, BackButtonListenerEvent } from '@capacitor/app';
import { Network, ConnectionStatus } from '@capacitor/network';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { MobilePlatformService } from './mobile-platform.service';

export interface NetworkStatus {
    connected: boolean;
    connectionType: string;
}

/**
 * Gerencia o ciclo de vida do app nativo:
 *  - App em background/foreground (AppState)
 *  - Botão voltar Android (back button)
 *  - Mudanças de rede (Network)
 *  - Teclado (Keyboard resize / insets)
 *  - StatusBar e SplashScreen
 *  - Haptics
 *
 * Deve ser inicializado UMA vez via AppModule ou app.component.ts.
 */
@Injectable({ providedIn: 'root' })
export class MobileLifecycleService implements OnDestroy {
    private readonly platform = inject(MobilePlatformService);
    private readonly router = inject(Router);

    private readonly _appState$ = new Subject<AppState>();
    private readonly _networkStatus$ = new Subject<NetworkStatus>();
    private readonly _keyboardHeight$ = new Subject<number>();
    private readonly _destroy$ = new Subject<void>();

    readonly appState$ = this._appState$.asObservable();
    readonly networkStatus$ = this._networkStatus$.asObservable();
    readonly keyboardHeight$ = this._keyboardHeight$.asObservable();

    private backButtonListenerHandle: any = null;

    async initialize(): Promise<void> {
        if (!this.platform.isNative()) return;

        await Promise.all([
            this.setupAppListeners(),
            this.setupNetworkListener(),
            this.setupKeyboardListeners(),
            this.configureStatusBar(),
        ]);

        await this.hideSplashScreen();
    }

    private async setupAppListeners(): Promise<void> {
        await App.addListener('appStateChange', (state: AppState) => {
            this._appState$.next(state);
        });

        this.backButtonListenerHandle = await App.addListener(
            'backButton',
            (event: BackButtonListenerEvent) => this.handleBackButton(event),
        );
    }

    private handleBackButton(event: BackButtonListenerEvent): void {
        const currentUrl = this.router.url;
        const isRoot = currentUrl === '/' || currentUrl === '/home' || currentUrl === '/index';

        if (isRoot) {
            App.minimizeApp();
        } else {
            this.router.navigate(['..'], { relativeTo: null });
        }
    }

    private async setupNetworkListener(): Promise<void> {
        const status = await Network.getStatus();
        this._networkStatus$.next({
            connected: status.connected,
            connectionType: status.connectionType,
        });

        await Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
            this._networkStatus$.next({
                connected: status.connected,
                connectionType: status.connectionType,
            });
        });
    }

    private async setupKeyboardListeners(): Promise<void> {
        if (!this.platform.isPluginAvailable('Keyboard')) return;

        await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
            this._keyboardHeight$.next(info.keyboardHeight);
            document.documentElement.style.setProperty(
                '--keyboard-height',
                `${info.keyboardHeight}px`,
            );
        });

        await Keyboard.addListener('keyboardWillHide', () => {
            this._keyboardHeight$.next(0);
            document.documentElement.style.setProperty('--keyboard-height', '0px');
        });
    }

    private async configureStatusBar(): Promise<void> {
        if (!this.platform.isPluginAvailable('StatusBar')) return;

        await StatusBar.setStyle({ style: Style.Dark });

        if (this.platform.isAndroid()) {
            await StatusBar.setBackgroundColor({ color: '#1e293b' });
        }
    }

    async hideSplashScreen(): Promise<void> {
        if (!this.platform.isPluginAvailable('SplashScreen')) return;
        await SplashScreen.hide({ fadeOutDuration: 500 });
    }

    setStatusBarStyle(dark: boolean): Promise<void> {
        if (!this.platform.isPluginAvailable('StatusBar')) return Promise.resolve();
        return StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
    }

    async getCurrentNetworkStatus(): Promise<NetworkStatus> {
        if (!this.platform.isNative()) {
            return { connected: navigator.onLine, connectionType: 'unknown' };
        }
        const status = await Network.getStatus();
        return { connected: status.connected, connectionType: status.connectionType };
    }

    // ─── Haptics ─────────────────────────────────────────────────────────────

    impact(style: ImpactStyle = ImpactStyle.Medium): Promise<void> {
        if (!this.platform.isPluginAvailable('Haptics')) return Promise.resolve();
        return Haptics.impact({ style });
    }

    vibrate(): Promise<void> {
        if (!this.platform.isPluginAvailable('Haptics')) return Promise.resolve();
        return Haptics.vibrate({ duration: 100 });
    }

    notificationHaptic(type: NotificationType = NotificationType.Success): Promise<void> {
        if (!this.platform.isPluginAvailable('Haptics')) return Promise.resolve();
        return Haptics.notification({ type });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();

        if (this.backButtonListenerHandle) {
            this.backButtonListenerHandle.remove();
        }

        App.removeAllListeners();
        Network.removeAllListeners();

        if (this.platform.isPluginAvailable('Keyboard')) {
            Keyboard.removeAllListeners();
        }
    }
}
